import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import * as Yup from 'yup'

import { DateHelper, DatePicker, Select } from '@loadsmart/loadsmart-ui'
import { type Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import { Divider, Drawer, Field, Icon, LabeledValue, Layout, Text } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { type OpenDockRescheduleSubmittedMisscheduling } from '../../domain/Appointment'
import { type OpenDockStopAppointment, type OpenDockWarehouseDetails } from '../../domain/Shipment'
import { mapAppointmentDateAndTimeToApi } from '../../infra/ShipmentMapper'
import {
  formatScheduledAt,
  getStartTimesFromDocks,
} from '../../presenters/openDockAppointmentPresenter'
import { useGetOpenDockWarehouseAvailability } from '../hooks/useGetOpenDockWarehouseAvailability'
import { useRescheduleOpenDockWarehouseAppointment } from '../hooks/useRescheduleOpenDockWarehouseAppointment'
import { RectangleSkeleton } from '../OpenDockAppointmentDrawer/RectangleSkeleton'
import { ScheduleTimeList } from '../ScheduleTimeList/ScheduleTimeList'
import { OpenDockAppointmentSummary } from './OpenDockAppointmentSummary'

const Schema = Yup.object().shape({
  dock: Yup.object()
    .shape({ label: Yup.string().required(), value: Yup.string().required() })
    .nullable()
    .required(),
  appointmentTime: Yup.string().nullable().required('Required'),
})

const DatePickerWrapper = styled.div`
  [role='menubar'] {
    div:nth-child(1) {
      flex: 1;
    }
  }
`

export function Reschedule({
  onRescheduleSuccess,
  onBack,
  misschedulingReasonValue,
  misschedulingNote,
  misschedulingFormValid,
  stopAppointment,
  warehouseDetails,
  facilityName,
  facilityTimezone,
  shipmentUUID,
  stopIndex,
}: {
  onRescheduleSuccess: (submitted: OpenDockRescheduleSubmittedMisscheduling) => void
  onBack: () => void
  misschedulingReasonValue: string
  misschedulingNote: string | undefined
  misschedulingFormValid: boolean
  stopAppointment?: OpenDockStopAppointment
  warehouseDetails?: OpenDockWarehouseDetails
  facilityName?: string
  facilityTimezone?: string
  shipmentUUID: string
  stopIndex: number
}) {
  const [appointmentDate, setAppointmentDate] = useState<string | null>(DateHelper().toString())

  const {
    warehouseAvailability: docksAvailability,
    fetchOpenDockWarehouseAvailability,
    status: availabilityStatus,
  } = useGetOpenDockWarehouseAvailability()

  const { reschedule, requestStatus } = useRescheduleOpenDockWarehouseAppointment()

  const loadTypeId = stopAppointment?.loadType.id

  useEffect(() => {
    if (appointmentDate && loadTypeId) {
      fetchOpenDockWarehouseAvailability(shipmentUUID, stopIndex, {
        loadTypeId,
        appointmentDate,
        facilityTimezone,
      })
    }
  }, [
    appointmentDate,
    facilityTimezone,
    fetchOpenDockWarehouseAvailability,
    loadTypeId,
    shipmentUUID,
    stopIndex,
  ])

  const { values, setFieldValue, errors, touched, setFieldTouched, isValid } = useFormik<{
    dock: { value: string; label: string } | null
    appointmentTime: string | null
  }>({
    initialValues: {
      dock: stopAppointment
        ? { value: stopAppointment.dockId, label: stopAppointment.dockName }
        : null,
      appointmentTime: null,
    },
    onSubmit: () => undefined,
    validationSchema: Schema,
  })

  const currentLoadType = warehouseDetails?.loadTypes.find(loadType => loadType.id === loadTypeId)
  const allowCarrierDockSelection = currentLoadType?.allowCarrierDockSelection ?? true
  const dockOptions =
    currentLoadType?.docks.map(dock => ({ label: dock.name, value: dock.id })) ?? []

  const timeSlots = getStartTimesFromDocks(docksAvailability ?? [], values.dock?.value)
  const isLoadingAvailability = availabilityStatus === RequestStatus.Pending
  const hasTimeSlots = timeSlots.length > 0
  const showScheduleTimeList = Boolean(
    appointmentDate && availabilityStatus === RequestStatus.Done && hasTimeSlots
  )
  const showTimeSlotsUnavailable =
    !loadTypeId ||
    availabilityStatus === RequestStatus.Error ||
    (availabilityStatus === RequestStatus.Done && !hasTimeSlots)

  const hourValidationStatus =
    touched.appointmentTime && errors.appointmentTime ? ('danger' as const) : ('default' as const)

  const isSubmittingReschedule = requestStatus === RequestStatus.Pending

  const disableSubmit =
    !isValid ||
    !appointmentDate ||
    !facilityTimezone ||
    !misschedulingFormValid ||
    !misschedulingReasonValue ||
    isSubmittingReschedule

  async function handleConfirm() {
    if (
      !appointmentDate ||
      !values.appointmentTime ||
      !values.dock ||
      !facilityTimezone ||
      !misschedulingReasonValue
    ) {
      return
    }

    try {
      await reschedule({
        shipmentUUID,
        stopIndex,
        misschedulingReason: misschedulingReasonValue,
        misschedulingNote: misschedulingNote ?? undefined,
        dockId: values.dock.value,
        start: mapAppointmentDateAndTimeToApi(
          appointmentDate,
          values.appointmentTime,
          facilityTimezone
        ),
      })
      toast.success('Appointment rescheduled successfully')
      onRescheduleSuccess({
        misschedulingReasonValue,
        misschedulingNote,
      })
    } catch {
      toast.error('Could not reschedule appointment, try again later')
    }
  }

  return (
    <>
      <Drawer.Header>
        <Layout.Group align="center" gap="spacing-1">
          <Icon name="calendar" size="24px" />
          Reschedule
        </Layout.Group>
        <Drawer.Close />
      </Drawer.Header>
      <Drawer.Body>
        <Layout.Stack gap="spacing-4" paddingB="spacing-2">
          <OpenDockAppointmentSummary
            facilityName={facilityName}
            facilityTimezone={facilityTimezone}
            stopAppointment={stopAppointment}
            betweenLoadTypeAndScheduledAt={
              allowCarrierDockSelection ? (
                <Field label="Docks" size="small">
                  <Select
                    name="dock"
                    placeholder="Select dock"
                    options={dockOptions}
                    value={values.dock as Selectable | null}
                    onChange={({ target }) => {
                      setFieldValue('dock', target.value as { value: string; label: string } | null)
                      setFieldValue('appointmentTime', null)
                    }}
                    hideClear
                  />
                </Field>
              ) : (
                <LabeledValue>
                  <LabeledValue.Label>Docks</LabeledValue.Label>
                  <LabeledValue.Value>{values.dock?.label ?? '-'}</LabeledValue.Value>
                </LabeledValue>
              )
            }
          />
          <Divider />
          <Field label="New appointment date" size="small">
            <DatePickerWrapper>
              <DatePicker
                name="appointmentDate"
                value={appointmentDate}
                onChange={({ target }) => {
                  setFieldValue('appointmentTime', null)
                  setAppointmentDate(target.value)
                }}
                constraints={[[null, DateHelper().subtract('day', 1).toString()]]}
              />
            </DatePickerWrapper>
          </Field>
          {isLoadingAvailability && <RectangleSkeleton />}
          {showTimeSlotsUnavailable && (
            <Text variant="body-sm" color="color-danger-60">
              No available times to show for this date. Please pick another date or try again later.
            </Text>
          )}
          {showScheduleTimeList && (
            <ScheduleTimeList
              label={
                <Text variant="body-sm" color="color-text-tertiary">
                  New hour
                </Text>
              }
              timeSlots={timeSlots}
              setValue={(value: string | null) => {
                setFieldTouched('appointmentTime')
                setFieldValue('appointmentTime', value)
              }}
              value={values.appointmentTime}
              validationStatus={hourValidationStatus}
              errorMessage={errors.appointmentTime}
            />
          )}
          <Divider />
          <Layout.Box
            paddingY="spacing-4"
            paddingX="spacing-6"
            borderWidth="border-thin"
            borderRadius="border-radius-m"
            borderColor="color-border-input"
          >
            <Layout.Stack gap="spacing-1">
              <Text variant="body-md-bold">Once you confirm, your changes will be saved</Text>
              {appointmentDate && values.appointmentTime && (
                <Text
                  variant="body-sm"
                  color="color-text-tertiary"
                  style={{ wordBreak: 'break-all' }}
                >
                  {`New schedule: ${formatScheduledAt(
                    appointmentDate,
                    values.appointmentTime,
                    facilityTimezone
                  )}`}
                </Text>
              )}
            </Layout.Stack>
          </Layout.Box>
        </Layout.Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.ActionTertiary
          style={{ marginRight: 'auto' }}
          leading={<Icon name="arrow-left" />}
          onClick={onBack}
        >
          Back
        </Drawer.ActionTertiary>
        <Drawer.ActionPrimary disabled={disableSubmit} onClick={() => void handleConfirm()}>
          Confirm
        </Drawer.ActionPrimary>
      </Drawer.Actions>
    </>
  )
}
